<template>
  <Transition
    appear
    name="fade"
  >
    <div
      v-if="showToast"
      class="vue-toast"
      :class="typeClass"
      data-pendo-ds="toast"
    >
      <div class="vue-toast__content-container">
        <div
          v-if="messageIcon"
          class="vue-toast__icon-container"
        >
          <BaseIcon
            color="inherit"
            :icon="messageIcon"
          />
        </div>
        <div class="vue-toast__message-container label-l-regular">
          {{ messageText }}
        </div>
      </div>
      <div class="vue-toast__buttons-container">
        <BaseButton
          v-if="hasAction"
          :emphasis="actionButtonEmphasis"
          :icon="actionIcon"
          :text="actionText"
          @click="actionHandler"
        />

        <BaseButton
          emphasis="low"
          icon="fa-close"
          theme="mono"
          @click="closeToast"
        />
      </div>
    </div>
  </Transition>
</template>

<script>
import BaseButton from './BaseButton.vue'
import BaseIcon from './BaseIcon.vue'

import { library } from '@fortawesome/fontawesome-svg-core'
import { faClose as fasClose } from '@fortawesome/pro-solid-svg-icons'
import { faClose as farClose } from '@fortawesome/pro-regular-svg-icons'
library.add(fasClose, farClose)

const TYPES = ['info', 'success', 'warning', 'error']
const DURATIONS = ['short', 'medium', 'long', 'extra-long', 'infinite']

export default {
  name: 'ToastContent',
  components: {
    BaseButton,
    BaseIcon,
  },
  props: {
    messageIcon: {
      type: String,
      required: false,
      default: '',
    },
    messageText: {
      type: String,
      required: true,
    },
    actionIcon: {
      type: String,
      required: false,
      default: '',
    },
    actionText: {
      type: String,
      required: false,
      default: '',
    },
    actionButtonEmphasis: {
      type: String,
      required: false,
      default: 'low',
    },
    action: {
      type: Function,
      required: false,
      default: () => {},
    },
    type: {
      type: String,
      required: false,
      default: 'info',
      validator: (value) => TYPES.includes(value),
    },
    duration: {
      type: String,
      required: false,
      default: 'medium',
      validator: (value) => DURATIONS.includes(value),
    },
  },
  emits: ['close'],
  data() {
    return {
      showToast: true,
    }
  },
  computed: {
    typeClass() {
      return this.type ? `vue-toast--type-${this.type}` : 'vue-toast--type-info'
    },
    durationTime() {
      switch (this.duration) {
        case 'short':
          return 3000
        case 'medium':
          return 4000
        case 'long':
          return 6000
        case 'extra-long':
          return 10000
        case 'infinite':
          return -1
        default:
          return 4000
      }
    },
    hasAction() {
      return this.actionText || this.actionIcon
    },
  },
  mounted() {
    if (this.durationTime === -1) return

    setTimeout(() => {
      this.closeToast()
    }, this.durationTime)
  },
  methods: {
    closeToast() {
      this.showToast = false
      setTimeout(() => {
        this.$emit('close')
      }, 500)
    },
    actionHandler() {
      this.action()
      this.closeToast()
    },
  },
}
</script>

<style lang="scss" scoped>
$type-colors: (
  "info": "candy-wrapper",
  "success": "spearmint",
  "warning": "clementine",
  "error": "chicago-dog-red"
);

.vue-toast {
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: var(--cr-br-medium);
  border: 1px solid;
  max-width: min(85vw, 800px);

  @each $type, $color in $type-colors {
    &--type-#{$type} {
      background-color: var(--#{$color}-100);
      border-color: var(--#{$color}-300);

      .vue-toast__icon-container {
        color: var(--#{$color}-1000);
      }
    }
  }
}

.vue-toast__content-container {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  padding: var(--space-2);
  margin-left: var(--space-2);
}

.vue-toast__message-container {
  white-space: normal;
  line-height: normal;

  &::first-letter {
    text-transform: capitalize;
  }
}

.vue-toast__buttons-container {
  padding: var(--space-2);
  display: flex;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
